import get from 'lodash/get';
import moment from 'moment';
import PropTypes from 'prop-types';
import tinycolor from 'tinycolor2';
import {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import config from '../../config/config';
import {truncate} from '../../helpers/stringUtils';


const heightPerTimeBlock = 20;
const heightTimeMins = 15;
const heightUnit = 'px';
const scrollElId = 'DailyBlockSchedule-Scrollable';

const times = [
  '12am',
  '1am',
  '2am',
  '3am',
  '4am',
  '5am',
  '6am',
  '7am',
  '8am',
  '9am',
  '10am',
  '11am',
  '12pm',
  '1pm',
  '2pm',
  '3pm',
  '4pm',
  '5pm',
  '6pm',
  '7pm',
  '8pm',
  '9pm',
  '10pm',
];

const calculateHeight = (minutes = 15) => {
  const timeBlocks = minutes / heightTimeMins;
  const height = timeBlocks * heightPerTimeBlock;
  return `${height}${heightUnit}`;
};

const calculateScroll = (hours) => {
  const timeBlocks = (hours * 60) / heightTimeMins;
  const height = timeBlocks * heightPerTimeBlock;
  return height;
};

const BufferBlock = props => {
  const {minutes} = props;
  const height = calculateHeight(minutes);
  return (
    <Box
      style={{
        height,
        borderRadius: '3px',
        background: 'rgba(0,0,0,0.10)',
        paddingLeft: '0.5em',
        paddingRight: '0.5em',
        paddingTop: '0px',
        paddingBottom: '0px',
        border: '1px solid rgba(0,0,0,0.15)',
        marginLeft: '20px',
        // fontSize: '0.8rem',
      }}
    >
      {
        minutes >= 15
        ? <Stack justifyContent='center'>
          <Typography
            variant='body2'
            style={{
              fontSize: '0.8rem',
              padding: 'none',
              margin: 'none',
              color: 'rgba(0,0,0,0.7)'
            }}
          >
            {minutes}-minute buffer
          </Typography>
        </Stack>
        : null
      }
    </Box>
  );
};

const VisitBlock = props => {
  const navigate = useNavigate();
  const {minutes, sessionType} = props;
  const height = calculateHeight(minutes);
  const color = get(sessionType, 'css_color', config().colors.secondary);
  const sessionId = get(props, 'appointment.session.id');
  return (
    <Stack direction='row'>
      <Box
        style={{
          height,
          color: '#fff',
          width: '100%',
          borderRadius: '3px',
          background: color,
          paddingLeft: '0.5em',
          paddingRight: '0.5em',
          border: `1px solid ${tinycolor(color).darken(10)}`,
        }}
        sx={{
          '&:hover': {
            opacity: 0.8,
            cursor: 'pointer',
          },
        }}
        onClick={
          sessionId
          ? () => navigate(`/providers/sessions/view/${sessionId}`)
          : () => (true)
        }
      >
        {
          minutes > 10
          ? <Stack justifyContent='center'>
            <Typography variant='body2'>
              {truncate(sessionType ? sessionType.title : '', {length: 20})}
            </Typography>
          </Stack>
          : null
        }
      </Box>
    </Stack>
  );
};

const TimeBlock = props => {
  const {appointment, sessionType, prevAppointment} = props;
  const visitStart = moment(appointment.start_time);
  const visitEnd = moment(appointment.end_time);
  const blockStart = moment(appointment.start_time_with_buffer);
  const blockEnd = moment(appointment.end_time_with_buffer);
  const visitDurationMinutes = moment.duration(visitEnd.diff(visitStart))
    .asMinutes();
  const fullDurationMins = (
    appointment.time_buffer_before_minutes
    + visitDurationMinutes
    + appointment.time_buffer_after_minutes
  );
  const prevStart = (
    prevAppointment
    ? moment(prevAppointment.end_time_with_buffer)
    : moment(blockStart).startOf('day')
  );
  const marginDuration = moment.duration(blockStart.diff(prevStart))
    .asMinutes();
  // const marginTop = '1em';
  const marginTop = calculateHeight(marginDuration);
  // const marginTop = start.subtract(prevStart)
  return (
    <Box
      style={{
        marginTop,
        width: '100%',
        maxWidth: '400px',
        height: calculateHeight(fullDurationMins),
      }}>
      <Stack spacing={0} padding={0}>
        {
          appointment.time_buffer_before_minutes
          ? <BufferBlock minutes={appointment.time_buffer_before_minutes} />
          : null
        }
        <VisitBlock
          appointment={appointment}
          minutes={visitDurationMinutes}
          sessionType={sessionType}
        />
        {
          appointment.time_buffer_after_minutes
          ? <BufferBlock minutes={appointment.time_buffer_after_minutes} />
          : null
        }
      </Stack>
    </Box>
  );
};

// Scroll to the start of the business day
const useAutoScroll = ({businessDayStartIndex, scrollElId}) => {
  useEffect(() => {
    const scroll = calculateScroll(businessDayStartIndex + 1);
    if (document) document.getElementById(scrollElId).scrollTo(0, scroll);
  }, []);
};

const DailyBlockSchedule = props => {
  const {
    appointments,
    sessionTypes,
    businessDayStartIndex,
    businessDayEndIndex
  } = props;
  useAutoScroll({businessDayStartIndex, scrollElId});
  return (
    <Stack>
      <Box
        id={scrollElId}
        style={{
          background: 'rgba(0,0,0,0.03)',
          overflowY: 'scroll',
          maxHeight: '70vh',
          scrollbarWidth: 'thin',
        }}
      >
        <Stack direction='row'>
          <Stack>
            {times.map((t, i) =>
              <Box
                key={t}
                style={{
                  height: calculateHeight(60),
                  maxHeight: calculateHeight(60),
                  paddingLeft: '0.5em',
                }}
              >
                <Stack direction='row' alignItems='center' spacing={1}>
                  <Typography
                    variant='body2'
                    style={{
                      color: (
                        i < businessDayStartIndex || i > businessDayEndIndex
                        ? 'rgba(0,0,0,0.2)'
                        : null
                      ),
                    }}
                  >
                    {t}
                  </Typography>
                  <Box
                    style={{
                      height: '1px',
                      borderBottom: '1px solid rgba(0,0,0,0.15)',
                      width: '2em',
                    }}
                  />
                </Stack>
              </Box>
            )}
          </Stack>
          <Box style={{width: '100%', paddingLeft: '0.25em'}}>
            <Stack>
              {(appointments || []).map((a, i) =>
                <TimeBlock
                  key={a.id}
                  appointment={a}
                  prevAppointment={appointments[i - 1] || null}
                  sessionType={(sessionTypes || []).find(
                    s => s.id === a.session_type_id
                  )}
                />
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Stack>
  );
};

DailyBlockSchedule.defaultProps = {
  businessDayStartIndex: 8,
  businessDayEndIndex: 18,
};

DailyBlockSchedule.propTypes = {
  appointments: PropTypes.arrayOf(PropTypes.object),
  sessionTypes: PropTypes.arrayOf(PropTypes.object),
};

export default DailyBlockSchedule;
